/**
 * Shared decision logic for every "Generate with AI" entry point (Test Cases'
 * manual/automation generators and the Requirement generator).
 *
 * Both generators must agree on the same priority order so they can't drift
 * into different behaviour: entitlement is checked before application sync,
 * and generation only starts once both are satisfied. Centralising the
 * decision here (instead of duplicating the same `if` chain in each
 * component) is what keeps Test Cases and Requirements consistent.
 */

export type GenerationGateDecision =
  /** Account isn't entitled to the AI feature — show the upgrade prompt and stop. */
  | "entitlement_denied"
  /** A sync/crawl is already running for the application — block and show progress. */
  | "sync_in_progress"
  /** The application has never been synchronised (or sync failed) — open the scanner. */
  | "needs_sync"
  /** Entitled and synchronised — safe to start generation. */
  | "ready";

export interface GenerationGateInput {
  /** `subscriptionData.data.aiEnabled !== false` — defaults to allowed while unknown so the backend stays authoritative. */
  aiEnabled: boolean;
  /** Whether a sync/crawl is currently running for the selected application. */
  syncInProgress: boolean;
  /** Whether the selected application has usable sync context (e.g. `pageCount > 0` or a prior successful sync). */
  hasSyncContext: boolean;
}

/**
 * Resolves the next action for a "Generate with AI" click using the
 * project's required priority order:
 * 1. Entitlement
 * 2. Sync in progress
 * 3. Sync missing/failed
 * 4. Ready to generate
 */
export function resolveGenerationGate(
  input: GenerationGateInput,
): GenerationGateDecision {
  if (!input.aiEnabled) return "entitlement_denied";
  if (input.syncInProgress) return "sync_in_progress";
  if (!input.hasSyncContext) return "needs_sync";
  return "ready";
}
